import { useCallback, useContext, useMemo, useState } from "react"
import floti_light from "../../assets/floti_ligth.svg"
import floti_light_text from "../../assets/floti_light_text.svg"

import './index.scss'
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import Input from "../../components/Input"
import Button from "../../components/Button"
import { login } from "../../services/calls/auth"
import { updateTokens } from "../../contexts/UserContext/actions"
import UserContext from "../../contexts/UserContext/context"
import LoadingSpinner from "../../components/LoadingSpinner"
import { InputError } from "../../components/Input/types"

type LoginData = {
  username?: string
  password?: string
}

type LoginError = {
  username?: InputError
  password?: InputError
}

const Login = () => {
  const { t } = useTranslation();
  const { dispatch } = useContext(UserContext)

  const [data, setData] = useState<LoginData>()
  const [error, setError] = useState<LoginError>({})
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const onFormChange = useCallback((key: string, value?: string) => {
    setData({ ...data, [key]: value })
  }, [data])

  const validateForm = useCallback(() => {
    const error: LoginError = {}
    if (!data?.username) error.username = { type: 'error', message: t('Username is required') }
    if (!data?.password) error.password = { type: 'error', message: t('Password is required') }
    setError(error)
    return Object.keys(error).length === 0 
  }, [data])

  const flotiLogin = useCallback(async () => {
    try {
      const isValid = validateForm()
      if (isValid) {
        setIsLoading(true)
        const response = await login(data)
        dispatch(updateTokens(response))
      }
    } catch (error) {
      setError({
        password: { type: 'error', message: t('Either of your credentials are wrong') }
      })
    }
    setIsLoading(false)
  }, [data, validateForm])

  const inputMessage = useMemo(() => {
    const lookup: LoginError = {}
    if (!error.password) {
      const elem = (
        <div className="Login__main-forgot">
          <Link to={''}>
            {t('Forgot your password?')}
          </Link>
        </div>
      )
      lookup.password = elem
    }
    return lookup;
  }, [])

  const buttonLabel = useMemo(() => {
    if (isLoading) {
      return (
        <>
          {`${t('Loading')}...`}
          <LoadingSpinner />
        </>
      )
    }

    return t('Login')
  }, [isLoading])

  return (
    <div className="Login">
      <div className="Login__header">
        <img className="Login__header-logo" src={floti_light} alt="Floti logo" />
        <img src={floti_light_text} alt="Floti" />
      </div>
      <div className="Login__main">
        <div className="Login__main-welcome">{t('Welcome to your Floti dashboard')}</div>
        <div className="Login__main-form">
          <Input
            type="text"
            label={t('Username')}
            value={data?.username}
            onInput={(val: string) => onFormChange('username', val)}
            error={error.username}
          />
          <Input
            type="password"
            label={t('Password')}
            value={data?.password}
            onInput={(val: string) => onFormChange('password', val)}
            text={inputMessage.password}
            error={error.password}
          />
          <Button
            label={buttonLabel}
            type={isLoading ? 'ghost' : 'primary'}
            onClick={() => flotiLogin()}
          />
        </div>
      </div>
      <div className="Login__footer">
        <div className="Login__footer-copyright">
          © {t('Floti is a Trackwell product, all rights reserved')}
        </div>
        <div className="Login__footer-links">
          <div className="Login__footer-conditions">
            <Link to="">
              {t('Terms and conditions')}
            </Link>       
          </div>
          <div className="Login__footer-contact">
            <Link to="https://forms.monday.com/forms/f9e0ecc795cbcea4fd1dc4eb4665231a?r=use1">
              {t('Contact us')}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login
